import CloseIcon from '@mui/icons-material/Close';
import { Box, DialogTitle, IconButton } from '@mui/material';
import { alpha } from '@mui/material/styles';
import PropTypes from 'prop-types';

import colors from 'common/constants/colors';

/**
 * POM-6010 — the fullscreen review's header.
 *
 * POM-6534: one line — what this screen is, how many items it holds, and what to do on it. The
 * "nothing is written until you commit" rule moved to the Pending commit group, beside the rows
 * it is about.
 *
 * Close is the only control here. Commit lives at the end of the filter row, next to the grid
 * whose rows it commits — see BulkLotNdcCommitAction.
 *
 * The type scale is deliberately literal rather than MUI variants: perfectRxTheme sets MuseoSans
 * at 12px, and these values mirror `.mhead` in ../styles/bulkOverride.css.
 */
const HEAD = {
  title: {
    fontSize: 23,
    fontWeight: 700,
    letterSpacing: '-0.022em',
    lineHeight: 1.15,
    color: colors.reviewHeaderTitleInk,
  },
  selectedPill: {
    display: 'inline-flex',
    alignItems: 'center',
    height: 24,
    px: 1.25,
    borderRadius: 3,
    fontSize: 12.5,
    fontWeight: 600,
    color: 'primary.main',
    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
    whiteSpace: 'nowrap',
  },
  subtitle: {
    m: 0,
    fontSize: 14,
    fontWeight: 600,
    letterSpacing: '-0.004em',
    color: colors.reviewHeaderSubtitleInk,
    flex: '1 1 260px',
    minWidth: 0,
  },
};

const BulkLotNdcReviewHeader = (props) => {
  const { selectedCount, committing, onClose } = props;

  return (
    <DialogTitle
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 3.25,
        pt: 2.25,
        pb: 1.5,
      }}
    >
      {/* Wraps rather than overflowing: on a narrow window the subtitle drops under the title
          instead of pushing Close off the right edge. */}
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          columnGap: 1.5,
          rowGap: 0.5,
        }}
      >
        <Box sx={HEAD.title}>Review Lot &amp; NDC</Box>
        <Box component='span' sx={HEAD.selectedPill}>
          {`${selectedCount} selected`}
        </Box>
        <Box component='h2' sx={HEAD.subtitle}>
          Check each lot and NDC before the labels print.
        </Box>
      </Box>

      <IconButton
        aria-label='Close review'
        onClick={onClose}
        disabled={committing}
        // A quiet 30px square that only picks up a background on hover. Leaving the dialog is not
        // the action to advertise here.
        sx={{
          width: 30,
          height: 30,
          borderRadius: 1.5,
          color: colors.reviewHeaderCloseInk,
          flexShrink: 0,
        }}
      >
        <CloseIcon fontSize='small' />
      </IconButton>
    </DialogTitle>
  );
};

BulkLotNdcReviewHeader.propTypes = {
  selectedCount: PropTypes.number.isRequired,
  committing: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default BulkLotNdcReviewHeader;
